import { getRegistrationRequestBody } from '../../services/apiServiceUtils';
import { config } from '../../config';
import { useSendRegistrationMutation, useSendTestRegistrationMutation } from '../../services/registrationApiService';
import { useAppNavigate, useAppSelector } from '../../configuration/setup/hooks';
import { Region, regionSelector } from '../region/regionSlice';
import { useEffect } from 'react';
import { ROUTE_FINISHED } from '../app/routes/routes';
import { type FetchBaseQueryError, QueryStatus } from '@reduxjs/toolkit/query';
import Notification from '@rio-cloud/rio-uikit/Notification';
import { FormattedMessage } from 'react-intl';
import type { SerializedError } from '@reduxjs/toolkit';
import { ConsentsBackButton } from './elements/ConsentsBackButton';
import { ConsentsNextButton } from './elements/ConsentsNextButton';
import { PersonalExplanation } from '../personal/elements/PersonalExplanation';
import { NewsletterApprovedCheckbox } from './elements/NewsletterApprovedCheckbox.tsx';
import { TermsAcceptedCheckbox } from './elements/TermsAcceptedCheckbox.tsx';
import { CombinedEulaAcceptedAndServiceCareBookedCheckbox } from './elements/CombinedEulaAcceptedAndServiceCareBookedCheckbox.tsx';

const SOFT_FAILURE_MESSAGE_KEYS: Record<string, string> = {
    MAN_SERVICE_CARE_ACTIVATION_FAILED: 'intl-msg:request.finished.warning.servicecare',
    EULA_ACCEPTANCE_FAILED: 'intl-msg:request.finished.warning.servicecare',
};

export const ConsentsPage = () => {
    const navigate = useAppNavigate();

    const region = useAppSelector(regionSelector);
    const companyInfo = useAppSelector(state => state.companyInfo);
    const personalInfo = useAppSelector(state => state.personalInfo);

    const [
        sendRegistration,
        {
            data: registrationData,
            isLoading: isRegistrationLoading,
            isSuccess: isRegistrationSuccess,
            status: registrationStatus,
            error: registrationError,
        },
    ] = useSendRegistrationMutation();
    const [
        sendTestRegistration,
        {
            data: testRegistrationData,
            isLoading: isTestRegistrationLoading,
            isSuccess: isTestRegistrationSuccess,
            status: testRegistrationStatus,
            error: testRegistrationError,
        },
    ] = useSendTestRegistrationMutation();

    const isLoading = isRegistrationLoading || isTestRegistrationLoading;
    const isSuccess = isRegistrationSuccess || isTestRegistrationSuccess;

    const finishRegistration = () => {
        const registrationBody = getRegistrationRequestBody(region, companyInfo, personalInfo);
        if (config.isTestTenantMode) {
            sendTestRegistration(registrationBody);
        } else {
            sendRegistration(registrationBody);
        }
    };

    useEffect(() => {
        if (isSuccess) {
            handleWarnings(registrationData?.warnings);
            handleWarnings(testRegistrationData?.warnings);
            Notification.success(
                <FormattedMessage
                    id={'intl-msg:request.finished.successfully'}
                    defaultMessage={'Your account has been successfully created.'}
                />,
                { timeout: 60_000 }
            );
            navigate(ROUTE_FINISHED);
        }
    }, [isSuccess, registrationData, testRegistrationData, navigate]);

    useEffect(() => {
        if (registrationStatus === QueryStatus.pending || testRegistrationStatus === QueryStatus.pending) {
            Notification.info(<FormattedMessage id={'intl-msg:request.processing'} />);
        }
        if (registrationStatus === QueryStatus.rejected) {
            displayErrorNotification(registrationError, region);
        }
        if (testRegistrationStatus === QueryStatus.rejected) {
            displayErrorNotification(testRegistrationError, region);
        }
    }, [registrationStatus, testRegistrationStatus, registrationError, testRegistrationError, region]);

    return (
        <div className={'RegistrationPanelContainer panel-default panel panel-body padding-25'}>
            <form className={'ConsentPageContainer'}>
                <TermsAcceptedCheckbox className={'margin-top-20'} disabled={isLoading}>
                    <PersonalExplanation />
                </TermsAcceptedCheckbox>
                {region !== Region.latinamerica && (
                    <CombinedEulaAcceptedAndServiceCareBookedCheckbox disabled={isLoading} />
                )}
                <NewsletterApprovedCheckbox className={'margin-bottom-15'} disabled={isLoading} />
                <hr />
                <div className={'display-flex justify-content-between margin-top-20-sm'}>
                    <ConsentsBackButton isLoading={isLoading} />
                    <ConsentsNextButton isLoading={isLoading} onClick={finishRegistration} />
                </div>
            </form>
        </div>
    );
};

const isFetchBaseQueryError = (v: FetchBaseQueryError | SerializedError): v is FetchBaseQueryError => {
    return (v as FetchBaseQueryError).data !== undefined && typeof (v as FetchBaseQueryError).data === 'object';
};

interface RegistrationError {
    code: string;
    detail: string;
    status: number;
}

const isRegistrationError = (v: unknown): v is RegistrationError => {
    return (
        (v as RegistrationError).code !== undefined &&
        (v as RegistrationError).detail !== undefined &&
        (v as RegistrationError).status !== undefined
    );
};

const isKnownErrorCode = (i: string): boolean => {
    const knownErrorCodes = [
        'DUPLICATE_TAX_ID',
        'GENERAL_DUPLICATE',
        'DUPLICATE_COMPANY_NAME',
        'COUNTRY_TAX_ID_MISMATCH',
        'INVALID_TAX_ID',
        'USER_ALREADY_EXISTS',
        'USER_VALIDATION_ERROR',
        'SF_DUPLICATE_COMPANY_NAME',
        'SF_DUPLICATE_COMPANY_NAME_AND_DUPLICATE_ADDRESS',
    ];
    return knownErrorCodes.includes(i);
};

const ErrorDetail = {
    PHONE_NUMBER_UNSUPPORTED_REGION: 'PHONE_NUMBER_UNSUPPORTED_REGION',
    PHONE_NUMBER_INVALID_FORMAT: 'PHONE_NUMBER_INVALID_FORMAT',
    UNKNOWN: 'UNKNOWN',
} as const;

const parseErrorDetail = (errorDetail: string) => {
    if (errorDetail.includes('PHONE_NUMBER_UNSUPPORTED_REGION')) {
        return ErrorDetail.PHONE_NUMBER_UNSUPPORTED_REGION;
    } else if (errorDetail.includes('PHONE_NUMBER_INVALID_FORMAT')) {
        return ErrorDetail.PHONE_NUMBER_INVALID_FORMAT;
    }
    return ErrorDetail.UNKNOWN;
};

const displayErrorNotification = (
    error: FetchBaseQueryError | SerializedError | undefined,
    region: Region | undefined
) => {
    const messageId = getRegistrationErrorMessageId(error, region);

    Notification.error(<FormattedMessage id={messageId} />, { timeout: 60_000, hideProgressBar: false });
};

export const getRegistrationErrorMessageId = (
    error: FetchBaseQueryError | SerializedError | undefined,
    region: Region | undefined
) => {
    const fallbackMessageId =
        region === Region.latinamerica ? 'intl-msg:error.commonError.latinamerica' : 'intl-msg:registration.error.backend.default';

    if (error && isFetchBaseQueryError(error)) {
        if (isRegistrationError(error.data)) {
            if (isKnownErrorCode(error.data.code)) {
                const parsedDetail = parseErrorDetail(error.data.detail);

                if (error.data.code === 'INVALID_TAX_ID') {
                    return region === Region.latinamerica
                        ? 'intl-msg:registration.error.backend.INVALID_TAX_ID.LATAM'
                        : fallbackMessageId;
                } else if (error.data.code === 'DUPLICATE_TAX_ID') {
                    return region === Region.latinamerica
                        ? 'intl-msg:registration.error.backend.DUPLICATE_TAX_ID.LATAM'
                        : 'intl-msg:registration.error.backend.DUPLICATE_TAX_ID';
                }

                if (parsedDetail === ErrorDetail.UNKNOWN) {
                    return `intl-msg:registration.error.backend.${error.data.code}`;
                }
                return `intl-msg:registration.error.backend.${error.data.code}.${parsedDetail}`;
            }
        }
    }

    return fallbackMessageId;
};

const handleWarnings = (warnings: { code: string; message: string }[] | undefined) => {
    if (warnings === undefined) {
        return;
    }
    warnings
        .map(it => SOFT_FAILURE_MESSAGE_KEYS[it.code])
        .filter((item, i, ar) => item !== undefined && ar.indexOf(item) === i)
        .forEach(it => {
            Notification.warning(<FormattedMessage id={it} />, { timeout: 60_000, hideProgressBar: false });
        });
};
